import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { apiGet, apiPost } from "@/config";
import { useArticles } from "@/hooks/useArticles";
import { useVisitorStats } from "@/hooks/useVisitorStats";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useAdminActions } from "@/hooks/useAdminActions";
import SiteHeader from "@/components/SiteHeader";
import IntroSection from "@/components/IntroSection";
import FeaturedSection from "@/components/FeaturedSection";
import AdminToolbar from "@/components/AdminToolbar";
import ArticlesPagination from "@/components/ArticlesPagination";
import ArticleCard from "@/components/ArticleCard";
import ArticleModal from "@/components/ArticleModal";
import SettingsModal from "@/components/SettingsModal";
import LoginModal from "@/components/LoginModal";
import SubscribersPanel from "@/components/SubscribersPanel";
import ArchivedArticlesPanel from "@/components/ArchivedArticlesPanel";
import ArticlePage from "@/components/ArticlePage";

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await apiGet("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (username, password) => {
    const { data } = await apiPost("/auth/login", { username, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await apiPost("/auth/logout");
    setUser(null);
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

const ArticlesList = ({ articles, onEdit, onDelete, isAdmin }) => (
  <>
    <h3 className="text-2xl font-bold text-white mb-6">Latest Articles</h3>
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} />
      ))}
    </section>
    {articles.length === 0 && (
      <div className="text-center py-12">
        <p className="text-slate-500 text-lg">No articles yet.</p>
        {isAdmin && <p className="text-slate-600 mt-2">Click &quot;New Article&quot; to create your first post!</p>}
      </div>
    )}
  </>
);

const Home = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSubscribersPanel, setShowSubscribersPanel] = useState(false);
  const [viewingArticleId, setViewingArticleId] = useState(null);
  const { user, loading, login, logout } = useAuth();

  const articleHook = useArticles();
  const { articles, featuredArticles, currentPage, setCurrentPage, totalPages, fetchArticles, fetchFeaturedArticles, refreshAll } = articleHook;
  const { visitorStats, trackVisit, fetchStats } = useVisitorStats();
  const { siteSettings, fetchSettings, saveSettings } = useSiteSettings();

  const admin = useAdminActions(articleHook);

  useEffect(() => {
    fetchArticles(1);
    fetchFeaturedArticles();
    fetchSettings();
    trackVisit();
  }, [fetchArticles, fetchFeaturedArticles, fetchSettings, trackVisit]);

  useEffect(() => { fetchArticles(currentPage); }, [currentPage, fetchArticles]);
  useEffect(() => { if (user) fetchStats(); }, [user, fetchStats]);

  // Handle browser back/forward for article pages
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/article\/([a-f0-9]+)$/);
      if (match) {
        setViewingArticleId(match[1]);
      } else {
        setViewingArticleId(null);
      }
    };
    window.addEventListener("popstate", handlePopState);
    handlePopState(); // Check on mount
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateToArticle = (articleId) => {
    window.history.pushState({}, "", `/article/${articleId}`);
    setViewingArticleId(articleId);
    window.scrollTo(0, 0);
  };

  const navigateToHome = () => {
    window.history.pushState({}, "", "/");
    setViewingArticleId(null);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Article page view
  if (viewingArticleId) {
    return (
      <>
        <SiteHeader user={user} visitorStats={visitorStats} onLoginClick={() => setShowLoginModal(true)} onLogout={logout} />
        <div className="pt-20">
          <ArticlePage articleId={viewingArticleId} onBack={navigateToHome} />
        </div>
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLogin={login} />
      </>
    );
  }

  // Homepage view
  return (
    <div className="min-h-screen bg-slate-950">
      <SiteHeader user={user} visitorStats={visitorStats} onLoginClick={() => setShowLoginModal(true)} onLogout={logout} />
      <main className="pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <IntroSection siteSettings={siteSettings} isAdmin={!!user} onEditClick={() => setShowSettingsModal(true)} />
          <FeaturedSection featuredArticles={featuredArticles} onArticleClick={navigateToArticle} />
          {user && (
            <AdminToolbar
              onNewArticle={() => admin.setShowArticleModal(true)}
              onImportDocx={admin.handleImportDocx}
              onViewArchived={admin.handleOpenArchivedPanel}
              onDeleteAllArticles={admin.handleDeleteAllArticles}
              onViewSubscribers={() => setShowSubscribersPanel(true)}
            />
          )}
          <ArticlesList articles={articles} onEdit={admin.handleEditArticle} onDelete={admin.handleDeleteArticle} isAdmin={!!user} />
          <ArticlesPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </main>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLogin={login} />
      <ArticleModal isOpen={admin.showArticleModal} onClose={admin.closeArticleModal} onSave={admin.handleSaveArticle} article={admin.editingArticle} />
      <SettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} onSave={saveSettings} settings={siteSettings} />
      <SubscribersPanel isOpen={showSubscribersPanel} onClose={() => setShowSubscribersPanel(false)} />
      <ArchivedArticlesPanel
        isOpen={admin.showArchivedPanel}
        onClose={admin.handleCloseArchivedPanel}
        fetchArchivedArticles={admin.fetchArchivedArticles}
        onUnarchiveOne={admin.unarchiveArticle}
        onUnarchiveAll={admin.unarchiveAll}
        onRefresh={() => refreshAll(currentPage)}
      />
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <Home />
    </AuthProvider>
  );
}
